import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {string} */
  #collectionUrl = window.location.href;

  #navigationId = 0;

  #navigationFrame = 0;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('click', this.#handleSubcollectionClick);
    this.addEventListener('pointerover', this.#prefetchSubcollection);
    this.addEventListener('focusin', this.#prefetchSubcollection);
    this.addEventListener('scroll', this.#handleSubcollectionScroll, true);
    this.addEventListener(ThemeEvents.FilterUpdate, this.#cancelNavigation);
    window.addEventListener('popstate', this.#handleSubcollectionPopState);
    window.addEventListener('resize', this.#updateSubcollectionHint);
    this.setAttribute('initialized', '');
    requestAnimationFrame(this.#updateSubcollectionHint);
  }

  updatedCallback() {
    super.updatedCallback();
    if (!mediaQueryLarge.matches) this.#syncMobileLayoutControls();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cancelNavigation();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('click', this.#handleSubcollectionClick);
    this.removeEventListener('pointerover', this.#prefetchSubcollection);
    this.removeEventListener('focusin', this.#prefetchSubcollection);
    this.removeEventListener('scroll', this.#handleSubcollectionScroll, true);
    this.removeEventListener(ThemeEvents.FilterUpdate, this.#cancelNavigation);
    window.removeEventListener('popstate', this.#handleSubcollectionPopState);
    window.removeEventListener('resize', this.#updateSubcollectionHint);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    if (viewport === 'desktop') {
      requestIdleCallback(() => {
        sessionStorage.setItem(this.#layoutStorageKey(viewport), value);
      });
    }
  };

  /**
   * Keeps a visitor's chosen density on this collection without carrying a
   * dense/list choice into a different collection for the first time.
   * @param {'desktop'|'mobile'} viewport
   * @param {string} [path]
   * @returns {string}
   */
  #layoutStorageKey(viewport, path = window.location.pathname) {
    return `product-grid-view-${viewport}:${path}`;
  }

  /** @param {string} [path] */
  #restoreCollectionLayout(path = window.location.pathname) {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const saved = viewport === 'desktop' ? sessionStorage.getItem(this.#layoutStorageKey(viewport, path)) : null;
    const grid = this.refs.grid;

    if (!grid) return;

    const value = saved || 'default';
    grid.setAttribute('product-grid-view', value);
    const inputName = viewport === 'mobile' ? 'grid-mobile' : 'grid';
    this.querySelectorAll(`input[name="${inputName}"]`).forEach((option) => {
      if (option instanceof HTMLInputElement) option.checked = option.value === value;
    });
  }

  #syncMobileLayoutControls() {
    const value = this.refs.grid?.getAttribute('product-grid-view') || 'default';
    for (const option of this.querySelectorAll('input[name="grid-mobile"]')) {
      if (option instanceof HTMLInputElement) option.checked = option.value === value;
    }
  }

  /** @param {Event} event */
  #subcollectionLinkFromEvent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;

    const link = target.closest('.ourooh-subcollection-nav__link');
    return link instanceof HTMLAnchorElement ? link : null;
  }

  /** @param {Event} event */
  #prefetchSubcollection = (event) => {
    const link = this.#subcollectionLinkFromEvent(event);
    if (!link || link.matches('[aria-current="page"]')) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;

    if (navigator.connection?.saveData) return;
    sectionRenderer.getPageHTML(url).catch(() => {});
  };

  /** @param {MouseEvent} event */
  #handleSubcollectionClick = (event) => {
    const link = this.#subcollectionLinkFromEvent(event);
    if (
      !link ||
      link.matches('[aria-current="page"]') ||
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;

    event.preventDefault();
    // Put focus back on the new active link only when the link was followed from the
    // keyboard (a keyboard click has no click count). After a tap or mouse click, the
    // restored focus drew a focus underline under the active link's own underline.
    this.#navigateSubcollection(url, { push: true, restoreFocus: event.detail === 0 });
  };

  #handleSubcollectionPopState = () => {
    if (window.location.href === this.#collectionUrl) return;
    this.#navigateSubcollection(new URL(window.location.href), { push: false, restoreFocus: false });
  };

  #cancelNavigation = () => {
    this.#collectionUrl = window.location.href;
    this.#navigationId += 1;
    cancelAnimationFrame(this.#navigationFrame);
    this.classList.remove('is-collection-loading');
    this.removeAttribute('aria-busy');
  };

  #updateSubcollectionHint = () => {
    const nav = this.querySelector('.ourooh-subcollection-nav');
    const list = nav?.querySelector('.ourooh-subcollection-nav__list');
    if (!nav || !list) return;

    const hasMoreRight = Math.ceil(list.scrollLeft + list.clientWidth) < list.scrollWidth - 2;
    nav.classList.toggle('has-more-right', hasMoreRight);
  };

  /** @param {Event} event */
  #handleSubcollectionScroll = (event) => {
    if (event.target instanceof Element && event.target.matches('.ourooh-subcollection-nav__list')) {
      this.#updateSubcollectionHint();
    }
  };

  /**
   * Fetches the destination document and morphs only its collection section.
   * The real links remain intact as a progressive-enhancement fallback.
   * @param {URL} url
   * @param {{push: boolean, restoreFocus: boolean}} options
   */
  async #navigateSubcollection(url, options) {
    // Keep Shopify's section-rendering parameter out of the visible/history URL.
    // A refresh of a URL containing `section_id` returns only raw section HTML.
    const destinationUrl = new URL(url.href);
    destinationUrl.searchParams.delete('section_id');
    const navigationId = ++this.#navigationId;
    cancelAnimationFrame(this.#navigationFrame);
    sectionRenderer.cancelPendingRender(this.sectionId);

    this.classList.add('is-collection-loading');
    this.setAttribute('aria-busy', 'true');
    const endWait = window.ouroohLoading?.begin();

    try {
      const html = await sectionRenderer.getPageHTML(destinationUrl);
      if (navigationId !== this.#navigationId || !this.isConnected) return;

      const destination = new DOMParser().parseFromString(html, 'text/html');
      if (!destination.head.querySelector('title') || !destination.head.querySelector('link[rel="canonical"]')) {
        throw new Error('Collection navigation requires destination metadata');
      }

      await morphSection(this.sectionId, html);
      if (navigationId !== this.#navigationId || !this.isConnected) return;

      this.#collectionUrl = destinationUrl.href;
      if (options.push) history.pushState({ ouroohCollectionPath: destinationUrl.pathname }, '', destinationUrl);
      this.#updateDocumentMetadata(destination);

      this.resetPages();
      this.#restoreCollectionLayout(destinationUrl.pathname);
      window.__ouroohInitCollectionPagination?.();

      this.#navigationFrame = requestAnimationFrame(() => {
        if (navigationId !== this.#navigationId || !this.isConnected) return;
        const list = this.querySelector('.ourooh-subcollection-nav__list');
        const activeLink = list?.querySelector('[aria-current="page"]');

        if (list && activeLink && list.scrollWidth > list.clientWidth) {
          const centeredPosition = activeLink.offsetLeft - (list.clientWidth - activeLink.offsetWidth) / 2;
          list.scrollTo({ left: Math.max(0, centeredPosition), behavior: 'smooth' });
        }

        this.#updateSubcollectionHint();
        if (options.restoreFocus && activeLink instanceof HTMLElement) activeLink.focus({ preventScroll: true });
      });
    } catch (error) {
      if (navigationId === this.#navigationId && this.isConnected) window.location.assign(destinationUrl.href);
    } finally {
      endWait?.();
      if (navigationId === this.#navigationId) {
        this.classList.remove('is-collection-loading');
        this.removeAttribute('aria-busy');
      }
    }
  }

  /** @param {Document} destination - The server-rendered destination document. */
  #updateDocumentMetadata(destination) {
    const selector =
      'title, meta[name="description"], meta[name="robots"], meta[property^="og:"], meta[name^="twitter:"], link[rel="canonical"], link[rel="alternate"][hreflang]';
    const metadata = Array.from(destination.head.querySelectorAll(selector), (node) => document.importNode(node, true));
    for (const node of document.head.querySelectorAll(selector)) node.remove();
    document.head.append(...metadata);
  }

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    if (!mediaQueryLarge.matches) this.#syncMobileLayoutControls();
    grid.dispatchEvent(
      new CustomEvent('product-grid:layout-change', {
        bubbles: true,
        detail: { value },
      })
    );
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#restoreCollectionLayout();
    this.#setLayout(this.refs.grid?.getAttribute('product-grid-view') || 'default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
